import {
  type AudioClip,
  addOverlayClip,
  applyCaptionPreset,
  applySceneLayout,
  type BrandTokens,
  bindColor,
  bindTextStyle,
  brandCaptionPreset,
  CAPTION_PRESETS,
  type CaptionPreset,
  type CaptionsElement,
  type ClipColor,
  type ClipDevice,
  type ClipTransition,
  clipAt,
  clipContentAspect,
  clipDeviceProps,
  clone,
  compactOverlayTracks,
  createAudioClip,
  createCaptionsElement,
  createOverlayClip,
  createVideoClip,
  DEFAULT_TRANSITION_MS,
  deviceAspect,
  deviceCanRotate,
  deviceModel,
  devicePerspectives,
  duplicateClip,
  duplicateOverlayClip,
  EMPTY_TIMELINE,
  fillLayoutSlots,
  fitDeviceBox,
  insertClip,
  insertElements,
  insertionIndexAt,
  isImagePlaceholder,
  type LayoutAsset,
  type LayoutResult,
  layoutClips,
  moveClipToTrack,
  moveOverlayClip,
  moveOverlayToMain,
  type OverlayClip,
  type OverlayLayout,
  overlayClipEnd,
  overlayLayoutBox,
  placeOverlayClip,
  refitDeviceBox,
  removeAudioClip,
  removeClip,
  removeElements,
  removeOverlayClip,
  removeTimeRanges,
  resolveElementTracks,
  type SceneDocument,
  type SceneLayout,
  setTransition,
  silenceRangesInClips,
  splitAudioAt,
  splitClipAt,
  splitOverlayAt,
  storedColor,
  suggestClipDevice,
  type TimelineWord,
  type TranscriptMap,
  type TransitionKind,
  trackForNewClip,
  type VideoClip,
  videoDuration,
  type WordOverride,
  worldBox,
} from "@cva/shared";
import { toast } from "sonner";
import type { AssetInfo } from "../commands";
import type { EditorStore } from "../store";
import type { VideoStore } from "./store";

/** Timeline commands of the video editor (on top of the shared operations). */

/** Adds a video or still image to the main track, or music to the audio track at the playhead. */
export function addClipFromAsset(
  store: EditorStore,
  video: VideoStore,
  asset: AssetInfo,
  options: { index?: number; at?: number } = {},
) {
  if (store.getState().readOnly) return;
  if (asset.kind === "audio") {
    const clip = createAudioClip(
      { id: asset.id, durationMs: asset.durationMs ?? null, name: asset.name },
      { start: Math.round(options.at ?? video.getState().time) },
    );
    store.getState().update((draft) => {
      draft.timeline ??= structuredClone(EMPTY_TIMELINE);
      draft.timeline.audio.push(clip);
    });
    video.getState().selectClip({ track: "audio", id: clip.id });
    return;
  }
  if (asset.kind !== "video" && asset.kind !== "image") return;
  const clip = createVideoClip({
    id: asset.id,
    kind: asset.kind,
    durationMs: asset.durationMs ?? null,
    name: asset.name,
  });
  store.getState().update((draft) => insertClip(draft, clip, options.index));
  video.getState().selectClip({ track: "video", id: clip.id });
}

/**
 * Puts a video or still on a video track above the main track, at the
 * playhead (or `at`): on `track` (a new track above it when the place is
 * taken), or above the videos there but below the titles.
 */
export function addOverlayFromAsset(
  store: EditorStore,
  video: VideoStore,
  asset: AssetInfo,
  options: { at?: number; track?: number } = {},
) {
  if (store.getState().readOnly) return;
  if (asset.kind !== "video" && asset.kind !== "image" && asset.kind !== "svg") return;
  const { doc } = store.getState();
  const clip = createOverlayClip(
    { id: asset.id, kind: asset.kind, durationMs: asset.durationMs ?? null, name: asset.name },
    doc.format,
    { start: Math.max(0, Math.round(options.at ?? video.getState().time)) },
  );
  store.getState().update((draft) => {
    if (options.track === undefined) {
      clip.track = newClipTrack(draft, clip);
      addOverlayClip(draft, clip);
      return;
    }
    draft.timeline ??= clone(EMPTY_TIMELINE);
    draft.timeline.overlays ??= [];
    draft.timeline.overlays.push(clip);
    placeOverlayClip(draft, clip.id, clip.start, options.track);
    compactOverlayTracks(draft);
  });
  store.getState().select([]);
  video.getState().selectClip({ track: "overlay", id: clip.id });
}

/** The selected main track clip onto a new track on top, at the same time (the main track closes the gap). */
export function moveSelectedToTrack(store: EditorStore, video: VideoStore) {
  const selected = video.getState().selectedClip;
  if (selected?.track !== "video") return;
  const { doc } = store.getState();
  const entry = layoutClips(doc.timeline).find((item) => item.clip.id === selected.id);
  if (!entry) return;
  let created: string | null = null;
  store.getState().update((draft) => {
    const track = trackForNewClip(draft, entry.start, entry.end);
    created = moveClipToTrack(draft, selected.id, entry.start, track);
  });
  if (created) video.getState().selectClip({ track: "overlay", id: created });
}

/** The selected clip of an upper track into the main track, where the playhead (or `at`) is. */
export function moveSelectedToMain(store: EditorStore, video: VideoStore, at?: number) {
  const selected = video.getState().selectedClip;
  if (selected?.track !== "overlay") return;
  const index = insertionIndexAt(store.getState().doc.timeline, at ?? video.getState().time);
  let created: string | null = null;
  store.getState().update((draft) => {
    created = moveOverlayToMain(draft, selected.id, index);
  });
  if (created) video.getState().selectClip({ track: "video", id: created });
}

/** "S": splits the selected audio or overlay clip, or the main track clip under the playhead. */
export function splitAtPlayhead(store: EditorStore, video: VideoStore) {
  const { time, selectedClip } = video.getState();
  let created: string | null = null;
  store.getState().update((draft) => {
    created =
      selectedClip?.track === "audio"
        ? splitAudioAt(draft, selectedClip.id, time)
        : selectedClip?.track === "overlay"
          ? splitOverlayAt(draft, selectedClip.id, time)
          : splitClipAt(draft, time);
  });
  if (!created) {
    toast.info(
      "Hier lässt sich nichts teilen – setze den Abspielkopf in einen Clip (nicht in einen Übergang).",
    );
  }
}

export function deleteSelectedClip(store: EditorStore, video: VideoStore) {
  const selected = video.getState().selectedClip;
  if (!selected) return false;
  store.getState().update((draft) => {
    if (selected.track === "audio") removeAudioClip(draft, selected.id);
    else if (selected.track === "overlay") removeOverlayClip(draft, selected.id);
    else removeClip(draft, selected.id);
  });
  video.getState().selectClip(null);
  const duration = videoDuration(store.getState().doc);
  if (video.getState().time > duration) video.getState().seek(duration);
  return true;
}

export function duplicateSelectedClip(store: EditorStore, video: VideoStore) {
  const selected = video.getState().selectedClip;
  if (selected?.track !== "video" && selected?.track !== "overlay") return;
  let created: string | null = null;
  store.getState().update((draft) => {
    created =
      selected.track === "overlay"
        ? duplicateOverlayClip(draft, selected.id)
        : duplicateClip(draft, selected.id);
  });
  if (created) video.getState().selectClip({ track: selected.track, id: created });
}

/** Transition into the main track clip `clipId` (null: hard cut). */
export function applyTransition(
  store: EditorStore,
  clipId: string,
  transition: ClipTransition | null,
  merge?: string,
) {
  store
    .getState()
    .update(
      (draft) => setTransition(draft, clipId, transition),
      merge ? { merge: `transition-${clipId}-${merge}` } : undefined,
    );
}

/** The same transition at every cut of the main track. */
export function applyTransitionEverywhere(
  store: EditorStore,
  kind: TransitionKind | null,
  duration = DEFAULT_TRANSITION_MS,
) {
  const clips = layoutClips(store.getState().doc.timeline).slice(1);
  if (clips.length === 0) return 0;
  store.getState().update((draft) => {
    for (const entry of clips) {
      setTransition(draft, entry.clip.id, kind ? { kind, duration } : null);
    }
  });
  return clips.length;
}

// ---------------------------------------------------------------------------
// Clip settings (properties panel and the timeline's context menu)
// ---------------------------------------------------------------------------

/** Changes a main track clip. */
export function updateVideoClip(
  store: EditorStore,
  id: string,
  recipe: (clip: VideoClip) => void,
  merge?: string,
) {
  store.getState().update(
    (draft) => {
      const target = draft.timeline?.clips.find((item) => item.id === id);
      if (target) recipe(target);
    },
    merge ? { merge: `clip-${id}-${merge}` } : undefined,
  );
}

/**
 * Changes a clip on a track above. With `retime` (start, length, speed) the
 * clip moves up a track when it then overlaps a neighbour.
 */
export function updateOverlayClip(
  store: EditorStore,
  id: string,
  recipe: (clip: OverlayClip) => void,
  options: { merge?: string; retime?: boolean } = {},
) {
  store.getState().update(
    (draft) => {
      const target = draft.timeline?.overlays?.find((item) => item.id === id);
      if (!target) return;
      recipe(target);
      if (options.retime) moveOverlayClip(draft, id, target.start, target.track);
    },
    options.merge ? { merge: `overlay-${id}-${options.merge}` } : undefined,
  );
}

/** Changes a music clip. */
export function updateAudioClip(
  store: EditorStore,
  id: string,
  recipe: (clip: AudioClip) => void,
  merge?: string,
) {
  store.getState().update(
    (draft) => {
      const target = draft.timeline?.audio.find((item) => item.id === id);
      if (target) recipe(target);
    },
    merge ? { merge: `audio-${id}-${merge}` } : undefined,
  );
}

/** Colour of a main track or overlay clip (undefined: original colours). */
export function setClipColor(
  store: EditorStore,
  track: "video" | "overlay",
  id: string,
  next: ClipColor | undefined,
  merge?: string,
) {
  store.getState().update(
    (draft) => {
      const target =
        track === "video"
          ? draft.timeline?.clips.find((item) => item.id === id)
          : draft.timeline?.overlays?.find((item) => item.id === id);
      if (!target) return;
      const value = storedColor(next);
      if (value) target.color = value;
      else delete target.color;
    },
    merge ? { merge: `color-${id}-${merge}` } : undefined,
  );
}

/** The same colour on every clip of the video; returns the number of clips. */
export function applyColorEverywhere(store: EditorStore, color: ClipColor | undefined) {
  let count = 0;
  store.getState().update((draft) => {
    const value = storedColor(color);
    for (const target of [...(draft.timeline?.clips ?? []), ...(draft.timeline?.overlays ?? [])]) {
      if (value) target.color = structuredClone(value);
      else delete target.color;
      count++;
    }
  });
  return count;
}

/** Puts a clip of a track above into a position preset (corner, half, full frame). */
export function applyOverlayLayout(
  store: EditorStore,
  id: string,
  layout: OverlayLayout,
  source: { width: number; height: number },
) {
  const frame = store.getState().doc.format;
  updateOverlayClip(store, id, (target) => {
    if (target.device) {
      // A device keeps its shape: the largest one that fits the preset's box.
      const aspect = deviceAspect(clipDeviceProps(target.device), clipContentAspect(source));
      const box = overlayLayoutBox(layout, frame, { width: aspect * 1000, height: 1000 });
      target.box = fitDeviceBox(box, aspect, frame);
    } else {
      target.box = overlayLayoutBox(layout, frame, source);
    }
    target.framing = { ...target.framing, x: 0, y: 0, zoom: 1 };
  });
}

/** The shape (width / height) of a clip's box: the device's, or the picture's. */
export function overlayBoxShape(
  clip: Pick<OverlayClip, "device">,
  source: { width: number; height: number },
): { width: number; height: number } {
  if (!clip.device) return source;
  const aspect = deviceAspect(clipDeviceProps(clip.device), clipContentAspect(source));
  return { width: aspect * 1000, height: 1000 };
}

/**
 * Shows a clip of an upper track in a device mockup (null: without). Turning
 * it on picks the device matching the picture and fits the box to the
 * device; changes keep the box's centre (`refitDeviceBox`).
 */
export function setOverlayDevice(
  store: EditorStore,
  id: string,
  device: ClipDevice | null,
  source: { width: number | null; height: number | null } | undefined,
  merge?: string,
) {
  const frame = store.getState().doc.format;
  const contentAspect = clipContentAspect(source);
  updateOverlayClip(
    store,
    id,
    (target) => {
      if (!device) {
        delete target.device;
        return;
      }
      // Unset options are left out (no `undefined` in the document).
      const next = Object.fromEntries(
        Object.entries(device).filter(([, value]) => value !== undefined),
      ) as ClipDevice;
      const before = target.device;
      const reshaped =
        !before ||
        before.device !== next.device ||
        before.landscape !== next.landscape ||
        before.tilt.x !== next.tilt.x ||
        before.tilt.y !== next.tilt.y ||
        before.tilt.z !== next.tilt.z;
      if (reshaped) {
        target.box = before
          ? refitDeviceBox(target.box, before, next, contentAspect, frame)
          : fitDeviceBox(target.box, deviceAspect(clipDeviceProps(next), contentAspect), frame);
      }
      target.device = next;
    },
    merge ? { merge: `device-${merge}` } : {},
  );
}

/** The default device for a clip: matching the picture's size (phone, tablet, laptop …). */
export function defaultClipDevice(asset: AssetInfo | undefined): ClipDevice {
  return suggestClipDevice(asset?.width, asset?.height);
}

/**
 * A main track clip into a device: it moves onto its own track above (the
 * main track closes the gap, like "Auf eigene Spur legen") and gets the
 * device matching its picture – one undo step.
 */
export function showMainClipInDevice(
  store: EditorStore,
  video: VideoStore,
  id: string,
  asset: AssetInfo | undefined,
) {
  const { doc } = store.getState();
  const entry = layoutClips(doc.timeline).find((item) => item.clip.id === id);
  if (!entry || store.getState().readOnly) return;
  const device = defaultClipDevice(asset);
  const contentAspect = clipContentAspect(asset);
  let created: string | null = null;
  store.getState().update((draft) => {
    const track = trackForNewClip(draft, entry.start, entry.end);
    created = moveClipToTrack(draft, id, entry.start, track);
    const target = draft.timeline?.overlays?.find((item) => item.id === created);
    if (!target) return;
    target.box = fitDeviceBox(
      target.box,
      deviceAspect(clipDeviceProps(device), contentAspect),
      draft.format,
    );
    target.device = device;
  });
  if (created) {
    video.getState().selectClip({ track: "overlay", id: created });
    toast.info("Der Clip liegt jetzt im Gerät auf einer eigenen Spur über der Hauptspur.");
  }
}

/** Phones and tablets stand in the video's orientation; other devices keep theirs. */
function landscapeFor(device: string, source: AssetInfo, fallback: boolean): boolean {
  const model = deviceModel(device);
  if (!deviceCanRotate(model) || !source.width || !source.height || source.width === source.height)
    return fallback;
  return source.width > source.height !== model.screen.width > model.screen.height;
}

/**
 * A video on a device mockup (dropped onto it, chosen in its panel): videos
 * only play on clips, so the mockup becomes a clip in that device on its own
 * track on top – same device, colour and view, at the mockup's place and
 * start. One undo step.
 */
export function videoIntoDevice(
  store: EditorStore,
  video: VideoStore,
  elementId: string,
  asset: AssetInfo,
): boolean {
  const { doc, readOnly } = store.getState();
  const element = doc.elements[elementId];
  if (readOnly || element?.type !== "device" || element.locked || asset.kind !== "video") {
    return false;
  }
  const box = worldBox(doc, elementId) ?? element;
  const before: ClipDevice = {
    device: element.device,
    ...(element.finish ? { finish: element.finish } : {}),
    landscape: element.landscape,
    tilt: { ...element.tilt },
    glare: element.glare,
    ...(element.url ? { url: element.url } : {}),
  };
  const device: ClipDevice = {
    ...before,
    landscape: landscapeFor(element.device, asset, element.landscape),
  };
  const clip = createOverlayClip(
    { id: asset.id, kind: "video", durationMs: asset.durationMs ?? null, name: asset.name },
    doc.format,
    {
      start: Math.max(0, Math.round(element.time?.start ?? video.getState().time)),
      // Where the element lay in the stack.
      track: resolveElementTracks(doc).get(elementId) ?? 1,
    },
  );
  const current = { x: box.x, y: box.y, width: box.width, height: box.height };
  clip.box = refitDeviceBox(current, before, device, clipContentAspect(asset), doc.format);
  clip.device = device;
  store.getState().update((draft) => {
    removeElements(draft, [elementId]);
    addOverlayClip(draft, clip);
  });
  store.getState().select([]);
  video.getState().selectClip({ track: "overlay", id: clip.id });
  toast.info("Das Video läuft jetzt im Gerät – als Clip auf einer eigenen Spur.");
  return true;
}

/** Name of the image placeholder a speaker video goes into ("Sprecher" in the reel templates). */
export const SPEAKER_SLOT = "Sprecher";

/**
 * A video on an image placeholder (e.g. the speaker's circle next to a phone
 * mockup): a clip on its own track on top in the placeholder's box – a round
 * placeholder becomes a circle, a rectangle keeps its corners – from the
 * placeholder's start (or the beginning). One undo step.
 */
export function videoIntoPlaceholder(
  store: EditorStore,
  video: VideoStore,
  elementId: string,
  asset: AssetInfo,
): boolean {
  const { doc, readOnly } = store.getState();
  const element = doc.elements[elementId];
  if (readOnly || !isImagePlaceholder(element) || element.locked || asset.kind !== "video") {
    return false;
  }
  const box = worldBox(doc, elementId) ?? element;
  const round = element.shape === "ellipse";
  const size = Math.min(box.width, box.height);
  const clip = createOverlayClip(
    { id: asset.id, kind: "video", durationMs: asset.durationMs ?? null, name: asset.name },
    doc.format,
    {
      start: Math.max(0, Math.round(element.time?.start ?? 0)),
      // Where the placeholder lay in the stack.
      track: resolveElementTracks(doc).get(elementId) ?? 1,
      box: round
        ? {
            x: Math.round(box.x + (box.width - size) / 2),
            y: Math.round(box.y + (box.height - size) / 2),
            width: Math.round(size),
            height: Math.round(size),
          }
        : {
            x: Math.round(box.x),
            y: Math.round(box.y),
            width: Math.round(box.width),
            height: Math.round(box.height),
          },
      radius: round ? Math.round(size / 2) : Math.round(element.radius),
    },
  );
  store.getState().update((draft) => {
    removeElements(draft, [elementId]);
    addOverlayClip(draft, clip);
  });
  store.getState().select([]);
  video.getState().selectClip({ track: "overlay", id: clip.id });
  toast.info("Das Video läuft jetzt im Platzhalter – als Clip auf einer eigenen Spur.");
  return true;
}

/**
 * A video straight into a device (Mockups panel): a clip on a new track on
 * top at the playhead, in the given device and view (default: the device
 * matching the video's size), fitted into the frame.
 */
export function addDeviceClip(
  store: EditorStore,
  video: VideoStore,
  asset: AssetInfo,
  options: { device?: string; view?: string; landscape?: boolean } = {},
) {
  if (store.getState().readOnly) return;
  if (asset.kind !== "video" && asset.kind !== "image") return;
  const { doc } = store.getState();
  const suggested = defaultClipDevice(asset);
  const id = options.device ?? suggested.device;
  const perspectives = devicePerspectives(id);
  const device: ClipDevice = {
    ...suggested,
    device: id,
    landscape: options.landscape ?? landscapeFor(id, asset, false),
    tilt: { ...(perspectives.find((entry) => entry.id === options.view) ?? perspectives[0]!).tilt },
  };
  const clip = createOverlayClip(
    { id: asset.id, kind: asset.kind, durationMs: asset.durationMs ?? null, name: asset.name },
    doc.format,
    { start: Math.max(0, Math.round(video.getState().time)) },
  );
  clip.box = fitDeviceBox(
    clip.box,
    deviceAspect(clipDeviceProps(device), clipContentAspect(asset)),
    doc.format,
  );
  clip.device = device;
  store.getState().update((draft) => {
    clip.track = newClipTrack(draft, clip);
    addOverlayClip(draft, clip);
  });
  store.getState().select([]);
  video.getState().selectClip({ track: "overlay", id: clip.id });
}

/** Track for a new clip: above the videos it overlaps, below the titles there. */
function newClipTrack(draft: SceneDocument, clip: OverlayClip): number {
  return trackForNewClip(draft, clip.start, overlayClipEnd(clip));
}

export type CornerShape = "square" | "rounded" | "circle";

/** The corner shape of a clip on a track above. */
export function overlayCornerShape(clip: Pick<OverlayClip, "box" | "radius">): CornerShape {
  const short = Math.min(clip.box.width, clip.box.height);
  if (clip.radius <= 0) return "square";
  const square = Math.abs(clip.box.width - clip.box.height) <= 2;
  return square && clip.radius >= short / 2 - 1 ? "circle" : "rounded";
}

/**
 * Corners of a clip on a track above: square, rounded or a circle (the box
 * becomes a square around the same centre).
 */
export function setOverlayCorners(store: EditorStore, id: string, shape: CornerShape) {
  const frame = store.getState().doc.format;
  updateOverlayClip(store, id, (target) => {
    const short = Math.min(target.box.width, target.box.height);
    if (shape === "square") target.radius = 0;
    else if (shape === "rounded") {
      target.radius = Math.round(Math.min(short / 4, Math.min(frame.width, frame.height) * 0.035));
    } else {
      const cx = target.box.x + target.box.width / 2;
      const cy = target.box.y + target.box.height / 2;
      target.box = {
        x: Math.round(cx - short / 2),
        y: Math.round(cy - short / 2),
        width: Math.round(short),
        height: Math.round(short),
      };
      target.radius = Math.round(short / 2);
    }
  });
}

// ---------------------------------------------------------------------------
// Screen layouts ("Bildaufteilung")
// ---------------------------------------------------------------------------

/**
 * Applies a screen layout to the main track clip at `t` (or to every clip).
 * Areas without a picture wait in the video store until they are filled in
 * the "Layout" tab. Returns the result (null: no clip at `t`).
 */
export function applyLayoutAt(
  store: EditorStore,
  video: VideoStore,
  layout: SceneLayout,
  t: number,
  options: { allClips?: boolean } = {},
): LayoutResult | null {
  let result: LayoutResult | null = null;
  const spaced = video.getState().layoutSpaced;
  store.getState().update((draft) => {
    result = applySceneLayout(draft, layout.id, t, { spaced, allClips: options.allClips });
  });
  const applied = result as LayoutResult | null;
  if (!applied) {
    toast.info(
      "Lege zuerst ein Video in die Hauptspur – die Aufteilung gilt für den Clip am Abspielkopf.",
    );
    return null;
  }
  const entry = clipAt(layoutClips(store.getState().doc.timeline), t);
  if (entry) {
    store.getState().select([]);
    video.getState().selectClip({ track: "video", id: entry.clip.id });
  }
  video.getState().setPendingLayout(
    applied.missing.length > 0
      ? {
          layout: layout.label,
          slots: applied.missing,
          range: { start: applied.start, end: applied.end },
          total: layout.slots,
        }
      : null,
  );
  if (applied.unused > 0) {
    toast.info(
      applied.unused === 1
        ? "Ein weiterer Clip liegt zur selben Zeit darüber – er behält seinen Platz."
        : `${applied.unused} weitere Clips liegen zur selben Zeit darüber – sie behalten ihren Platz.`,
    );
  }
  return applied;
}

/** Puts a video or image into the next empty area of the pending screen layout. */
export function fillPendingLayout(store: EditorStore, video: VideoStore, asset: LayoutAsset) {
  const pending = video.getState().pendingLayout;
  if (!pending) return;
  const [slot, ...rest] = pending.slots;
  if (!slot) return;
  let created: string[] = [];
  store.getState().update((draft) => {
    created = fillLayoutSlots(draft, [slot], [asset], pending.range);
  });
  if (created[0]) video.getState().selectClip({ track: "overlay", id: created[0] });
  video.getState().setPendingLayout(rest.length > 0 ? { ...pending, slots: rest } : null);
}

/** Presets offered in the editor: the brand's first, when there is a kit. */
export function captionPresets(brand: BrandTokens | null): CaptionPreset[] {
  const own = brandCaptionPreset(brand);
  return own ? [own, ...CAPTION_PRESETS] : [...CAPTION_PRESETS];
}

/** Applies a preset; the brand preset stays linked to the kit (font, accent colour). */
export function applyPreset(
  element: CaptionsElement,
  preset: CaptionPreset,
  brand: BrandTokens | null,
) {
  applyCaptionPreset(element, preset);
  if (preset.id === "brand" && brand) {
    bindTextStyle(element, "heading-1", brand, 1);
    bindColor(
      element,
      "fill",
      brand.colors.some((color) => color.key === "accent") ? "accent" : "primary",
      brand,
    );
  }
}

/** Adds the captions element (once per video) or applies a preset to it. */
export function applyCaptions(store: EditorStore, preset?: CaptionPreset) {
  const { doc, brand } = store.getState();
  const existing = Object.values(doc.elements).find((element) => element.type === "captions");
  const chosen = preset ?? captionPresets(brand)[0]!;
  if (existing) {
    store.getState().update((draft) => {
      const element = draft.elements[existing.id];
      if (element?.type === "captions") applyPreset(element, chosen, brand);
    });
    store.getState().select([existing.id]);
    return existing.id;
  }
  const element = createCaptionsElement(doc.format, chosen);
  if (brand && chosen.id === "brand") applyPreset(element, chosen, brand);
  store.getState().update((draft) => insertElements(draft, 0, [element]));
  store.getState().select([element.id]);
  return element.id;
}

/** Correction of one transcript word. Empty fields are removed. */
export function setWordOverride(store: EditorStore, key: string, patch: WordOverride) {
  store.getState().update(
    (draft) => {
      if (!draft.timeline) return;
      const overrides = draft.timeline.captions.overrides;
      const next = { ...overrides[key], ...patch };
      for (const field of Object.keys(next) as (keyof WordOverride)[]) {
        if (next[field] === undefined || next[field] === false) delete next[field];
      }
      if (Object.keys(next).length === 0) delete overrides[key];
      else overrides[key] = next;
    },
    { merge: `word-${key}` },
  );
}

export type WordPatches = Readonly<Record<string, WordOverride>>;

/** Word corrections into a draft. Empty fields are removed. */
export function mergeWordOverrides(draft: SceneDocument, patches: WordPatches) {
  if (!draft.timeline) return;
  const overrides = draft.timeline.captions.overrides;
  for (const [key, patch] of Object.entries(patches)) {
    const next = { ...overrides[key], ...patch };
    for (const field of Object.keys(next) as (keyof WordOverride)[]) {
      if (next[field] === undefined || next[field] === false) delete next[field];
    }
    if (Object.keys(next).length === 0) delete overrides[key];
    else overrides[key] = next;
  }
}

/** Several word corrections as one step (a corrected caption text). */
export function applyWordOverrides(store: EditorStore, patches: WordPatches) {
  if (Object.keys(patches).length === 0) return;
  store.getState().update((draft) => mergeWordOverrides(draft, patches));
}

/** Hides or shows words (a whole caption or a selection in it). */
export function setWordsHidden(
  store: EditorStore,
  words: readonly TimelineWord[],
  hidden: boolean,
) {
  applyWordOverrides(
    store,
    Object.fromEntries(words.map((word) => [word.key, { hidden: hidden || undefined }])),
  );
}

/** Back to the recognised text of the words: corrections, hidden words and breaks removed. */
export function resetWords(store: EditorStore, words: readonly TimelineWord[]) {
  applyWordOverrides(
    store,
    Object.fromEntries(
      words.map((word) => [
        word.key,
        { text: undefined, hidden: undefined, breakBefore: undefined },
      ]),
    ),
  );
}

/**
 * "Schneiden per Text": removes the time from the first to the last of the
 * words from the video (the pauses between them included).
 */
export function cutWords(
  store: EditorStore,
  words: readonly TimelineWord[],
  patches: WordPatches = {},
) {
  if (words.length === 0) return 0;
  const start = Math.min(...words.map((word) => word.start));
  const end = Math.max(...words.map((word) => word.end));
  let removed = 0;
  store.getState().update((draft) => {
    // Corrections typed before cutting belong to the same step.
    mergeWordOverrides(draft, patches);
    removed = removeTimeRanges(draft, [{ start, end }]);
  });
  return removed;
}

/** Shows or hides the captions in the video (all captions elements). */
export function setCaptionsHidden(store: EditorStore, hidden: boolean) {
  store.getState().update((draft) => {
    for (const element of Object.values(draft.elements)) {
      if (element.type === "captions") element.hidden = hidden;
    }
  });
}

/**
 * "Stille entfernen": cuts pauses longer than `minPauseMs` between spoken
 * words, only inside clips with a transcript (stills and B-roll stay).
 */
export function removeSilences(
  store: EditorStore,
  transcripts: TranscriptMap,
  minPauseMs: number,
  paddingMs = 120,
) {
  const doc = store.getState().doc;
  const ranges = silenceRangesInClips(doc, transcripts, minPauseMs, paddingMs);
  if (ranges.length === 0) return 0;
  let removed = 0;
  store.getState().update((draft) => {
    removed = removeTimeRanges(draft, ranges);
  });
  return removed;
}
